import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { readApiError } from '../../core/api-error';
import { CsrWebApiService } from '../../csr-web-api.service';
import { LearningFeedbackFieldsComponent } from '../../features/learner/learning/components/learning-feedback-fields.component';
import { LearningFeedbackService } from '../../features/learner/learning/services/learning-feedback.service';
import { FeedbackMetadataResponse } from '../../models';

export interface MandatoryFeedbackDialogData {
  metadata?: FeedbackMetadataResponse | null;
  skillGapSessionId?: string;
  counsellingSessionId?: string;
  learningPlanId?: string;
  careerChallengeId?: string;
}

@Component({
  selector: 'app-mandatory-feedback-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, LearningFeedbackFieldsComponent],
  providers: [LearningFeedbackService],
  templateUrl: './mandatory-feedback-dialog.component.html',
  styleUrl: './mandatory-feedback-dialog.component.scss',
})
export class MandatoryFeedbackDialogComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<MandatoryFeedbackDialogComponent, boolean>,
  );
  readonly data: MandatoryFeedbackDialogData | null = inject(MAT_DIALOG_DATA, {
    optional: true,
  });
  private readonly api = inject(CsrWebApiService);
  readonly feedbackService = inject(LearningFeedbackService);

  loading = true;
  submitting = false;
  errorMessage = '';

  async ngOnInit(): Promise<void> {
    try {
      if (this.data?.metadata?.schemas?.length) {
        await this.feedbackService.loadFromMetadata(this.data.metadata);
      } else {
        await this.feedbackService.loadMandatoryFeedback();
      }

      if (!this.feedbackService.hasFeedback) {
        // If there is no pending feedback, automatically resolve and close
        this.dialogRef.close(true);
      }
    } catch (error) {
      this.errorMessage = readApiError(
        error,
        'Could not load feedback requirements.',
      );
    } finally {
      this.loading = false;
    }
  }

  async submit(): Promise<void> {
    const missing = this.feedbackService.getMissingRequiredLabel();
    if (missing) {
      this.errorMessage = `Please provide feedback for: ${missing}`;
      return;
    }

    this.submitting = true;
    this.errorMessage = '';

    try {
      const validAnswers = this.feedbackService.getValidVisibleAnswers();
      const appArea = (
        this.data?.metadata?.appArea ??
        this.feedbackService.feedback?.appArea ??
        ''
      )
        .toString()
        .toLowerCase();

      let skillGapSessionId = this.data?.skillGapSessionId;
      let counsellingSessionId = this.data?.counsellingSessionId;
      let learningPlanId = this.data?.learningPlanId;

      if (!skillGapSessionId && !counsellingSessionId && !learningPlanId) {
        try {
          const profile = await firstValueFrom(this.api.getLearnerProfile());
          skillGapSessionId = profile.latestSgaId ?? undefined;
          counsellingSessionId = profile.latestCpfId ?? undefined;
          learningPlanId = profile.latestPlanId ?? undefined;
        } catch {
          // If profile fetch fails, fallback to onboarding feedback
        }
      }

      if (appArea === 'learning_plan' || (!appArea && learningPlanId)) {
        if (!learningPlanId) {
          throw new Error('Learning plan id is required for feedback.');
        }
        await firstValueFrom(
          this.api.recordLearningPlanFeedback({
            learningPlanId,
            feedback: validAnswers,
          }),
        );
      } else if (
        appArea === 'skill_gap_analysis' ||
        (!appArea && skillGapSessionId)
      ) {
        if (!skillGapSessionId) {
          throw new Error('Skill gap session id is required for feedback.');
        }
        await firstValueFrom(
          this.api.recordSgaFeedback({
            sessionId: skillGapSessionId,
            feedback: validAnswers,
          }),
        );
      } else if (
        appArea === 'career_path_finder' ||
        (!appArea && counsellingSessionId)
      ) {
        if (!counsellingSessionId) {
          throw new Error(
            'Career path finder session id is required for feedback.',
          );
        }
        await firstValueFrom(
          this.api.recordCareerPathFinderFeedback({
            sessionId: counsellingSessionId,
            feedback: validAnswers,
          }),
        );
      } else if (
        appArea === 'career_challenge' ||
        (!appArea && this.data?.careerChallengeId)
      ) {
        const challengeId =
          this.data?.careerChallengeId ??
          this.data?.metadata?.targetId ??
          '';
        if (!challengeId) {
          throw new Error('Career challenge id is required for feedback.');
        }
        await firstValueFrom(
          this.api.recordCareerChallengeFeedback({
            careerChallengeId: challengeId,
            feedback: validAnswers,
          }),
        );
      } else {
        await firstValueFrom(
          this.api.recordOnboardingFeedback({
            feedback: validAnswers,
          }),
        );
      }

      this.feedbackService.clear();
      this.dialogRef.close(true);
    } catch (error) {
      this.errorMessage = readApiError(
        error,
        'Could not submit feedback. Please try again.',
      );
    } finally {
      this.submitting = false;
    }
  }
}
